/**
 * Guided Learning semantic events (GL-1 §1) with the GL-P PAYLOAD ALLOWLIST
 * enforced AT EMIT. Event payloads carry ONLY the fields listed in the §1 table
 * below — NEVER WHY / free text, answer text, card text, or direct identifiers.
 * `userIdHash` is the sole subject key. This is structural, not advisory: the
 * builder copies ONLY allow-listed keys off the caller's input, so a forbidden
 * field (e.g. `why`, `userId`, `answerText`) cannot reach a sink even if a call
 * site passes it by mistake. The payload-schema tests pin this.
 *
 * Transport: events flow through the EXISTING stats/audit path — there is NO new
 * analytics store in this tranche, and the third-party tag containers are
 * suppressed on exam/deck routes (GL-P P10), so GL events must NOT ride the
 * suppressed GTM/FB path. Instead a single injectable sink is registered by the
 * app when GL is live (and by GL-2 for the /gl/why + /gl/hint server surface);
 * with no sink registered the emit is a safe no-op (the dark default). The
 * allow-list enforcement runs regardless of whether a sink is present.
 */

export const GL_VERSION = 1;

const ENVELOPE_KEYS = ["userIdHash", "questionId", "quizId", "platform"];
const PLATFORMS = new Set(["web", "ios"]);

// The §1 table, verbatim: event name -> allowed payload keys (beyond the
// envelope) with a validator per key. A key whose value fails its validator is
// DROPPED (never coerced into a string, so no free text can sneak through a
// wrong-typed field). Optional keys (marked with `?` in §1) are simply absent
// when not supplied.
const bool = (v) => typeof v === "boolean";
const nonNegInt = (v) => typeof v === "number" && Number.isFinite(v) && v >= 0 && Number.isInteger(v);
const hintIndex = (v) => v === 0 || v === 1;
const id = (v) => (typeof v === "string" && v.length > 0) || typeof v === "number";
const oneOf = (...allowed) => (v) => allowed.includes(v);

export const GL_EVENT_ALLOWLIST = {
  "gl.why.submitted": { whyLen: nonNegInt, thin: bool, reaskShown: bool },
  "gl.why.reasked": { whyLen: nonNegInt },
  "gl.why.skipped": { afterReask: bool },
  "gl.hint.requested": { hintIndex },
  "gl.hint.served": { hintIndex, cacheHit: bool, latencyMs: nonNegInt },
  "gl.hint.blocked": { reason: oneOf("cap", "flag", "error"), capValue: nonNegInt },
  "gl.card.drafted": { cardId: id, latencyMs: nonNegInt },
  "gl.card.accepted": { cardId: id, editDistance: nonNegInt },
  "gl.card.edited": { cardId: id, editDistance: nonNegInt },
  "gl.card.discarded": { cardId: id, editDistance: nonNegInt },
  "gl.deck.due_served": { cardId: id, surface: oneOf("web", "drive") },
  "gl.deck.recall_recorded": {
    cardId: id,
    outcome: oneOf("correct", "incorrect", "skipped"),
    surface: oneOf("web", "drive")
  }
};

export const isKnownGlEvent = (name) =>
  Object.prototype.hasOwnProperty.call(GL_EVENT_ALLOWLIST, name);

let sink = null;
/** Register the transport (app bootstrap / GL-2). Pass null to detach. */
export const setGlEventSink = (fn) => {
  sink = typeof fn === "function" ? fn : null;
};
/** The currently-registered sink (null if none) — for tests/introspection. */
export const getGlEventSink = () => sink;

/**
 * Build the sanitized, allow-listed event record for `name` from `fields`.
 * Returns null for an unknown event name (nothing is ever emitted for one).
 * Exported so tests can assert the exact shape without a sink.
 */
export const buildGlEvent = (name, fields = {}) => {
  const allowed = GL_EVENT_ALLOWLIST[name];
  if (!allowed) {
    return null;
  }

  // `userIdHash` is the MANDATORY subject key (GL-1 §1 / GL-P). A record without
  // it does not satisfy the envelope and must never be emitted — validate at
  // emit so a live mount that forgot to supply it fails loud (null), rather than
  // silently shipping an unattributable event to the stats/audit path.
  const subject = fields.userIdHash;
  if (typeof subject !== "string" || subject.length === 0) {
    return null;
  }

  const record = {
    event: name,
    ts: new Date().toISOString(),
    glVersion: GL_VERSION,
    userIdHash: subject
  };

  // Envelope: only the allow-listed context keys, validated. `platform` defaults
  // to "web"; `userId` (raw) and any other identifier are NOT envelope keys, so
  // they can never be copied in. (`userIdHash` is set above as mandatory.)
  for (const key of ENVELOPE_KEYS) {
    if (key === "userIdHash") {
      continue;
    }
    const value = fields[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (key === "platform") {
      if (PLATFORMS.has(value)) {
        record.platform = value;
      }
    } else if (id(value)) {
      record[key] = value;
    }
  }
  if (!record.platform) {
    record.platform = "web";
  }

  // Payload: ONLY this event's allow-listed keys, each validated.
  for (const [key, validate] of Object.entries(allowed)) {
    const value = fields[key];
    if (value !== undefined && value !== null && validate(value)) {
      record[key] = value;
    }
  }

  return record;
};

/**
 * Enforce the allow-list, then dispatch to the registered sink (no-op if none).
 * @returns the sanitized record (or null for an unknown event).
 */
export const emitGlEvent = (name, fields = {}) => {
  const record = buildGlEvent(name, fields);
  if (record && sink) {
    try {
      sink(record);
    } catch (_) {
      // A telemetry sink must never break the exam flow.
    }
  }
  return record;
};
